import { memo } from 'react';
import { difference } from 'lodash-es';
import { Skeleton } from '@ds';
import { useMultiSuggestStatistics } from '@lp/features/MultiSuggest/hooks';
import { ListEmptyState } from '@lp/features/MultiSuggest/components/ListEmptyState';
import { StatisticsItem } from './StatisticsItem';

export type StatisticsListProps = {
  taskId: string;
  caseIds: string[];
  logisticsIds: string[];
  chainNumber: number;
};

export const StatisticsList = memo(
  ({ taskId, caseIds, logisticsIds, chainNumber }: StatisticsListProps) => {
    const { statisticsItems, isLoading } = useMultiSuggestStatistics({
      taskId,
      caseIds,
      logisticsIds,
      chainNumber,
    });

    if (isLoading) {
      return (
        <div className="flex flex-col gap-2 pb-8 w-full">
          <Skeleton className="w-2/3 h-4 rounded-lg" />
          <Skeleton className="w-full h-4 rounded-lg" />
          <Skeleton className="w-1/3 h-4 rounded-lg" />
          <Skeleton className="w-3/4 h-4 rounded-lg" />
          <Skeleton className="w-3/5 h-4 rounded-lg" />
          <Skeleton className="w-5/6 h-4 rounded-lg" />
          <Skeleton className="w-2/3 h-4 rounded-lg" />
        </div>
      );
    }

    if (!statisticsItems.length) {
      return (
        <div className="flex flex-col gap-2 pb-8 w-full bg-base-90">
          <ListEmptyState iconBgClass="bg-base-70" iconColorClass="text-base-40" message="Nothing found" />
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-3">
        <span className="text-m-cps text-base-20">INFO</span>
        {statisticsItems.map((data) => (
          <StatisticsItem key={data.type} data={data} />
        ))}
      </div>
    );
  },
  (prev, next) => {
    const areCaseIdsEqual = !difference(prev.caseIds, next.caseIds).length;
    const areLogisticsIdsEqual = !difference(prev.logisticsIds, next.logisticsIds).length;
    const isChainNumberEqual = prev.chainNumber === next.chainNumber;

    return areCaseIdsEqual && areLogisticsIdsEqual && isChainNumberEqual;
  },
);
